import { Script, Vec3, math } from 'playcanvas';
import { isDesktop } from './utils.js';


const slotsDesktop = {
    full: { height: isDesktop() ? 1 : 0.8, distance: 8 },
    gender: { height: isDesktop() ? 1 : 0.8, distance: 8 },
    head: { height: 1.8, distance: 1.6 },
    hair: { height: 1.8, distance: 1.6 },
    top: { height: 1.5, distance: 2.0 },
    bottom: { height: 0.5, distance: 2.8 },
    shoes: { height: 0.5, distance: 1.6 },
};

const slotsMobile = {
    full: { height: isDesktop() ? 1 : 0.8, distance: 8 },
    gender: { height: isDesktop() ? 1 : 0.8, distance: 8 },
    head: { height: 1.6, distance: 1.6 },
    hair: { height: 1.6, distance: 1.6 },
    top: { height: 1.2, distance: 2.0 },
    bottom: { height: 0.3, distance: 2.8 },
    shoes: { height: 0.3, distance: 1.6 },
};


class Camera extends Script {
    initialize() {
        this.vec3A = new Vec3();

        // settings
        this.speed = 12;

        this.rotateSpeed = 0.3;
        this.panSpeed = 0.003;
        this.zoomSpeed = 0.005;

        this.tilt = -0.15;

        // ranges
        this.mobileMinMaxHeight = [0.4, 1.7];
        this.desktopMinMaxHeight = [0.6, 1.8];
        this.mobileMinMaxDistance = [1.4, 4.5];
        this.desktopMinMaxDistance = [1.4, 5.5];

        this.headLookAtStart = 1.1;
        this.mobileHeadLookAtHeight = 1.65;
        this.desktopHeadLookAtHeight = 1.65;
        this.feetLookAtStart = 0.7;
        // on desktop panel is on the right, so we can use entire screen height
        this.mobileFeetLookAtHeight = -0.15;
        this.desktopFeetLookAtHeight = 0.15;

        // input events
        this.app.on('input:drag', this.onDrag, this);
        this.app.on('input:zoom', this.onZoom, this);
        this.app.on('camera:setFocus', this.onFocus, this);
        this.app.on('camera:slotFocus', this.onSlotFocus, this);

        // initial values and values for lerping
        this.angleTarget = 0;
        this.heightTarget = this.entity.getLocalPosition().y;
        this.lookAtTarget = this.getLookAt();
        this.distanceTarget = 5;

        this.currentAngle = this.angleTarget;
        this.currentHeight = this.heightTarget;
        this.currentLookAt = this.lookAtTarget;
        this.currentDistance = this.distanceTarget;

        // resize
        this.app.graphicsDevice.on('resizecanvas', this.onResize, this);
        this.onResize();

        // higher focal length, better portrait view
        this.entity.camera.fov = 25;

        this.forcePortrait = false;
    }

    update(dt) {
        if (this.forcePortrait) {
            this.entity.camera.fov = 10;
            this.entity.setPosition(0, 1.67, 0.05);
            this.entity.setEulerAngles(5, 15, 0);
            this.entity.translateLocal(0, 0, 2);
            return;
        }

        // maximal value 1, to prevent overshooting
        const lerpValue = Math.min(1, this.speed * dt);

        this.currentAngle = math.lerp(this.currentAngle, this.angleTarget, lerpValue);
        this.currentHeight = math.lerp(this.currentHeight, this.heightTarget, lerpValue);
        this.currentLookAt = math.lerp(this.currentLookAt, this.lookAtTarget, lerpValue);
        this.currentDistance = math.lerp(this.currentDistance, this.distanceTarget, lerpValue);

        // orbit camera point around the center
        const x = Math.sin(this.currentAngle * math.DEG_TO_RAD) * this.currentDistance;
        const z = Math.cos(this.currentAngle * math.DEG_TO_RAD) * this.currentDistance;

        let distance = Math.max(0, Math.min(3, this.currentDistance - 1.5)) / 3;

        let height = math.lerp(this.currentHeight, this.defaultHeight, distance);
        let lookAt = math.lerp(this.currentLookAt, this.defaultLookAt, distance);

        this.entity.setLocalPosition(x, height, z);
        this.entity.lookAt(0, lookAt, 0);
    }

    onDrag(x, y) {
        this.orbitCamera(x);
        this.panCamera(y);
    }

    onZoom(delta) {
        this.distanceTarget = math.clamp(this.distanceTarget + delta * this.zoomSpeed, this.minMaxDistance[0], this.minMaxDistance[1]);
    }

    onSlotFocus(slot) {
        if (!this.slots[slot]) return;
        this.onFocus(this.slots[slot].height, this.slots[slot].distance);
    }

    onFocus(height, distance) {
        this.heightTarget = math.clamp(height, this.minMaxHeight[0], this.minMaxHeight[1]);
        this.distanceTarget = math.clamp(distance, this.minMaxDistance[0], this.minMaxDistance[1]);
        this.lookAtTarget = this.getLookAt();
    }

    orbitCamera(x) {
        if (!x) return;

        this.angleTarget = (this.angleTarget - (x * this.rotateSpeed));
    }

    panCamera(y) {
        if (!y) return;

        const min = this.minMaxHeight[0];
        const max = this.minMaxHeight[1];

        this.heightTarget = math.clamp(this.heightTarget + y * this.panSpeed, min, max);

        this.lookAtTarget = this.getLookAt();
    }

    getLookAt() {
        // after reaching head level, start tilting camera
        // if (this.heightTarget + this.tilt > this.headLookAtStart) {
        //     return this.remap(
        //         this.heightTarget + this.tilt,
        //         this.headLookAtStart, this.minMaxHeight[1],
        //         this.headLookAtStart, this.headLookAtHeight
        //     );
        // }

        // after reaching aproximately knees level, start tilting camera towards feet
        if (this.heightTarget < this.feetLookAtStart) {
            const height = this.feetLookAtHeight;

            // based on current value between minHeight and feetLookAtStart change tilt
            return this.remap(
                this.heightTarget,
                this.minMaxHeight[0], this.feetLookAtStart,
                height, this.feetLookAtStart + this.tilt
            );
        }

        return this.heightTarget + this.tilt;
    }

    onResize() {
        const height = this.app.graphicsDevice.height;
        const width = this.app.graphicsDevice.width;
        this.entity.camera.horizontalFov = (height / width) > 1;
        // on resize from desktop to mobile, feet can be covered by UI, so we need to adjust look at target
        this.lookAtTarget = this.getLookAt();
    }

    // remap value from one range to another
    remap(value, fromMin, fromMax, toMin, toMax) {
        return toMin + (toMax - toMin) * (value - fromMin) / (fromMax - fromMin);
    }

    get defaultHeight() {
        return isDesktop() ? 1.5 : 1.3;
    }

    get defaultLookAt() {
        return isDesktop() ? 0.9 : 0.7;
    }

    get minMaxHeight() {
        return isDesktop() ? this.desktopMinMaxHeight : this.mobileMinMaxHeight;
    }

    get minMaxDistance() {
        return isDesktop() ? this.desktopMinMaxDistance : this.mobileMinMaxDistance;
    }

    get headLookAtHeight() {
        return isDesktop() ? this.desktopHeadLookAtHeight : this.mobileHeadLookAtHeight;
    }

    get feetLookAtHeight() {
        return isDesktop() ? this.desktopFeetLookAtHeight : this.mobileFeetLookAtHeight;
    }

    get slots() {
        return isDesktop() ? slotsDesktop : slotsMobile;
    }
}

const script = {
    class: Camera,
    name: 'camera'
};

export default script;